import { InfoCircleOutlined } from '@ant-design/icons'
import { Col, DatePicker, Row } from 'antd'
import moment from 'moment'
import styled from 'styled-components'
import { FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Select } from 'sutra/components/Select/Select'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { FormikType } from 'features/project-form/features/source/components/NewSource'

type Props = {
  formik: FormikType
  sourceType: string
}

const dateFormat = 'MM/DD/YYYY'

const supportedLocales = [
  'ca_ES',
  'de_DE',
  'de_AT',
  'de_CH',
  'en_AE',
  'en_AU',
  'en_CA',
  'en_CH',
  'en_DE',
  'en_FI',
  'en_GB',
  'en_GR',
  'en_HK',
  'en_IE',
  'en_IN',
  'en_JP',
  'en_KW',
  'en_MY',
  'en_PH',
  'en_PK',
  'en_NZ',
  'en_SA',
  'en_SG',
  'en_US',
  'en_ZA',
  'fr_CA',
  'fr_FR',
  'fr_BE',
  'fr_CH',
  'fr_LU',
  'hi_IN',
  'nl_NL',
  'nl_BE',
  'pt_BR',
  'pt_PT',
  'ar_AE',
  'ar_AR',
  'ar_EG',
  'ar_SA',
  'cs_CZ',
  'da_DK',
  'el_GR',
  'es_AR',
  'es_ES',
  'es_MX',
  'es_US',
  'es_UY',
  'hu_HU',
  'it_CH',
  'it_IT',
  'jp_JP',
  'ja_JP',
  'ko_KR',
  'no_NO',
  'pl_PL',
  'ru_RU',
  'sv_SE',
  'th_TH',
  'tr_TR',
  'zh_CN',
  'zh_HK',
  'zh_TW',
  'ro_RO',
  'ii_ID',
  'fi_FI',
  'lt_LT',
]

export const PowerReviews = ({ formik }: Props): JSX.Element => {
  return (
    <>
      <FormItem
        label={
          <Tooltip content="Give your source a name to display in the charts. Make sure it's one you'll recognize.">
            <span>
              Source Title <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.sourceTitle ? 'error' : ''}
        help={formik.errors.sourceTitle}
        wrapperCol={{ span: 10 }}
      >
        <Input
          name="sourceTitle"
          onChange={formik.handleChange}
          value={formik.values.sourceTitle}
          $error={Boolean(formik.errors.sourceTitle)}
          placeholder="Insert title here"
        />
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Select the earliest date from which data should be pulled.">
            <span>
              Date <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        wrapperCol={{ span: 16 }}
        validateStatus={formik.errors.sourceDate ? 'error' : ''}
        help={formik.errors.sourceDate}
      >
        <DatePicker
          defaultValue={
            formik.values.sourceDate && moment(formik.values.sourceDate)
          }
          onChange={(value) => {
            if (value) {
              formik.setFieldValue('sourceDate', value.toDate())
            }
          }}
          format={dateFormat}
        />
      </FormItem>

      <Row justify="space-around">
        <Col span={11}>
          <FormItem
            label={
              <Tooltip content="Provide the Powerreviews Page ID.">
                <span>
                  Page <InfoCircleOutlined />
                </span>
              </Tooltip>
            }
            validateStatus={formik.errors.extra_product_id ? 'error' : ''}
            help={formik.errors.extra_product_id}
          >
            <Flex>
              <Input
                name="extra_product_id"
                onChange={formik.handleChange}
                value={formik.values.extra_product_id}
                $error={Boolean(formik.errors.extra_product_id)}
                placeholder="Insert Page ID here"
              />
            </Flex>
          </FormItem>
        </Col>
        <Col span={11}>
          <FormItem
            label={
              <Tooltip content="Provide the Powerreviews Enterprise API Secret.">
                <span>
                  Secret <InfoCircleOutlined />
                </span>
              </Tooltip>
            }
            validateStatus={formik.errors.access_token ? 'error' : ''}
            help={formik.errors.access_token}
          >
            <Input
              name="access_token"
              onChange={formik.handleChange}
              value={formik.values.access_token || ''}
              $error={Boolean(formik.errors.access_token)}
              placeholder="Insert API Key here"
            />
          </FormItem>
        </Col>
      </Row>
      <Row justify="space-around">
        <Col span={11}>
          <FormItem
            label={
              <Tooltip content="Provide the Powerreviews Enterprise Client ID.">
                <span>
                  Client <InfoCircleOutlined />
                </span>
              </Tooltip>
            }
            validateStatus={formik.errors.client_id ? 'error' : ''}
            help={formik.errors.client_id}
          >
            <Input
              name="client_id"
              onChange={formik.handleChange}
              value={formik.values.client_id || ''}
              $error={Boolean(formik.errors.client_id)}
              placeholder="Insert Client ID here"
            />
          </FormItem>
        </Col>

        <Col span={11}>
          <FormItem
            label={
              <Tooltip content="Reviews Locale.">
                <span>
                  Locale <InfoCircleOutlined />
                </span>
              </Tooltip>
            }
            validateStatus={formik.errors.locale ? 'error' : ''}
            help={formik.errors.locale}
          >
            <Select
              onChange={(option) => formik.setFieldValue('locale', option)}
              value={formik.values.locale}
              defaultValue="en_US"
              options={supportedLocales.map((locale) => ({
                value: locale,
                label: locale,
              }))}
            />
          </FormItem>
        </Col>
      </Row>
    </>
  )
}

const Flex = styled.div`
  display: flex;
  align-items: center;
`
